import usePartySocket from "partysocket/react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
	type Answers,
	type ClientMessage,
	type ClientRole,
	QUESTIONS,
	type Registration,
	type RegistrationErrors,
	type ServerMessage,
	type Stats,
	SURVEY_ROOM,
	emptyStats,
} from "../shared";

/** Welcome screen with the registration, one step per question, then the thank-you screen. */
export const WELCOME_STEP = 0;
export const DONE_STEP = QUESTIONS.length + 1;

export type Progress = {
	/** Survey round this progress belongs to; a reset on the server starts a new one. */
	epoch: string | null;
	/** Set once the server confirmed the registration; the questions stay locked until then. */
	firstName: string | null;
	step: number;
	answers: Answers;
	rank: number | null;
};

export type RegistrationState =
	| { status: "idle" }
	| { status: "pending" }
	| { status: "rejected"; errors: RegistrationErrors };

const PARTICIPANT_KEY = "papernaut-survey:participant";
const PROGRESS_KEY = "papernaut-survey:progress";

const freshProgress = (epoch: string | null): Progress => ({
	epoch,
	firstName: null,
	step: WELCOME_STEP,
	answers: {},
	rank: null,
});

function readStorage(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function writeStorage(key: string, value: string) {
	try {
		localStorage.setItem(key, value);
	} catch {
		// Private mode or full storage: the survey still works, it just cannot resume after a reload.
	}
}

function participantId(): string {
	const stored = readStorage(PARTICIPANT_KEY);
	if (stored) {
		return stored;
	}
	const id = crypto.randomUUID();
	writeStorage(PARTICIPANT_KEY, id);
	return id;
}

function loadProgress(): Progress {
	try {
		const stored = JSON.parse(readStorage(PROGRESS_KEY) ?? "null") as Partial<Progress> | null;
		if (stored && typeof stored.step === "number" && stored.answers) {
			return {
				epoch: stored.epoch ?? null,
				firstName: typeof stored.firstName === "string" ? stored.firstName : null,
				step: Math.min(Math.max(stored.step, WELCOME_STEP), DONE_STEP),
				answers: stored.answers,
				rank: stored.rank ?? null,
			};
		}
	} catch {
		// Corrupt entry: start over.
	}
	return freshProgress(null);
}

function sameAnswer(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
	return !!a && !!b && a.length === b.length && a.every((id) => b.includes(id));
}

/** Sends whatever the server is missing: answers given offline and the completion. */
function resendMissing(
	progress: Progress,
	server: { answers: Answers; rank: number | null },
	send: (message: ClientMessage) => void,
) {
	for (const [questionId, optionIds] of Object.entries(progress.answers)) {
		if (!sameAnswer(optionIds, server.answers[questionId])) {
			send({ type: "vote", questionId, optionIds });
		}
	}
	if (progress.step === DONE_STEP && server.rank === null) {
		send({ type: "complete" });
	}
}

function useSurveySocket(role: ClientRole, query: Record<string, string>, onMessage: (message: ServerMessage) => void) {
	const [connected, setConnected] = useState(false);
	const socket = usePartySocket({
		party: "survey",
		room: SURVEY_ROOM,
		query,
		onOpen: () => setConnected(true),
		onClose: () => setConnected(false),
		onMessage: (event: MessageEvent<string>) => onMessage(JSON.parse(event.data) as ServerMessage),
	});

	const send = useCallback(
		(message: ClientMessage) => {
			if (role === "participant") {
				socket.send(JSON.stringify(message));
			}
		},
		[role, socket],
	);

	return { connected, send };
}

export function useParticipantSurvey() {
	const [query] = useState(() => ({ pid: participantId() }));
	const [progress, setProgress] = useState(loadProgress);
	const [registration, setRegistration] = useState<RegistrationState>({ status: "idle" });
	const [stats, setStats] = useState<Stats>(emptyStats);

	// The socket handlers need the latest progress without re-creating the connection.
	const progressRef = useRef(progress);
	progressRef.current = progress;
	const sendRef = useRef<(message: ClientMessage) => void>(() => {});

	const update = useCallback((next: Progress) => {
		progressRef.current = next;
		setProgress(next);
	}, []);

	const { connected, send } = useSurveySocket("participant", query, (message) => {
		const current = progressRef.current;

		switch (message.type) {
			case "hello": {
				const { epoch, firstName, answers, rank } = message;
				if (current.epoch !== null && current.epoch !== epoch) {
					update({ ...freshProgress(epoch), firstName, answers, rank });
					return;
				}

				// Local answers win: they may have been given while the connection was down. Without a
				// registration the server would drop them, so they wait for "registered".
				if (firstName !== null) {
					setRegistration({ status: "idle" });
					resendMissing(current, { answers, rank }, sendRef.current);
				}
				update({
					...current,
					epoch,
					firstName,
					answers: { ...answers, ...current.answers },
					rank: rank ?? current.rank,
				});
				return;
			}
			case "registered":
				setRegistration({ status: "idle" });
				resendMissing(current, { answers: {}, rank: null }, sendRef.current);
				update({
					...current,
					firstName: message.firstName,
					step: current.step === WELCOME_STEP ? WELCOME_STEP + 1 : current.step,
				});
				window.scrollTo({ top: 0 });
				return;
			case "registrationRejected":
				setRegistration({ status: "rejected", errors: message.errors });
				return;
			case "stats":
				setStats(message.stats);
				return;
			case "completed":
				update({ ...current, rank: message.rank });
				return;
			case "reset":
				setRegistration({ status: "idle" });
				update(freshProgress(message.epoch));
				return;
		}
	});
	sendRef.current = send;

	useEffect(() => {
		writeStorage(PROGRESS_KEY, JSON.stringify(progress));
	}, [progress]);

	const register = useCallback(
		(details: Registration) => {
			setRegistration({ status: "pending" });
			send({ type: "register", ...details });
		},
		[send],
	);

	const vote = useCallback(
		(questionId: string, optionIds: string[]) => {
			const current = progressRef.current;
			update({ ...current, answers: { ...current.answers, [questionId]: optionIds } });
			send({ type: "vote", questionId, optionIds });
		},
		[send, update],
	);

	const goTo = useCallback(
		(step: number) => {
			const current = progressRef.current;
			const next = Math.min(Math.max(step, WELCOME_STEP), DONE_STEP);
			if (next === DONE_STEP && current.rank === null) {
				send({ type: "complete" });
			}
			update({ ...current, step: next });
			window.scrollTo({ top: 0 });
		},
		[send, update],
	);

	return { connected, progress, registration, stats, register, vote, goTo };
}

export function useLiveStats() {
	const [query] = useState(() => ({ role: "screen" }));
	const [stats, setStats] = useState<Stats>(emptyStats);
	const { connected } = useSurveySocket("screen", query, (message) => {
		if (message.type === "stats") {
			setStats(message.stats);
		}
	});
	return { connected, stats };
}
